import React, { useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { RouteComponentProps, useHistory } from 'react-router';
import { AlertContext } from 'lib/alert-context/alert-context';

import pause from 'four-nets/icons/svg/icon_pause.svg';
import play from 'four-nets/icons/svg/icon_play.svg';
import refresh from 'four-nets/icons/svg/icon_refresh.svg';
import pencil from 'four-nets/icons/svg/icon_edit.svg';
import replyIcon from 'four-nets/icons/svg/icon_reply.svg';
import heart from 'four-nets/icons/svg/icon_heart.svg';
import heartFull from 'four-nets/icons/svg/icon_heart-full.svg';
import trash from 'four-nets/icons/svg/icon_delete.svg';
import view from 'four-nets/icons/svg/icon_views-2.svg';

import { AuthOrigin } from 'four-nets/user/auth/auth-ga-events';

import SVGIcon from 'lib/images/svg-icon/svg-icon';
import Styled from 'styled';

import ActionButtonView from 'four-nets/ui/action-button/action-button-view';
import CreateEditProductView, {
    CreateEditProductViewModel,
} from 'four-nets/bazaar/product/create-edit-product/create-edit-product-view';
import { Product, ProductStore, SuperCategoryStore } from 'four-nets/bazaar/bazaar';
import { Site } from 'four-nets/site';

import actionButtonStyles from './product-actions-bar-button-styles.scss';
import defaultStyles from './product-actions-bar-view.scss';
import favouriteIconStyles from './product-actions-bar-view-favouriteicon.scss';
import withMarginStyles from './product-actions-bar-view-iconwithmargin.scss';

export interface Props extends RouteComponentProps {
    styles: typeof defaultStyles;
    product: Product;
    productStore: ProductStore;
    superCategoryStore: SuperCategoryStore;
    replyRef: any;
    authorView: React.ReactNode;
    isSuperstaff: boolean;
    contentHidden: boolean;
    showDialog?: (dialog: any) => void;
    showContent: () => void;
    forceUpdate: () => void;
}

const ProductActionsBarView: React.FC<Props> = (props, context) => {
    const { styles, product, authorView, isSuperstaff, contentHidden } = props;

    const { acknowledge, confirm, retry } = useContext(AlertContext);
    const history = useHistory();

    const editProductView = useRef<any>(null);
    const createEditProductViewModel = useRef(
        new CreateEditProductViewModel({
            product: props.product,
            productStore: props.productStore,
            superCategoryStore: props.superCategoryStore,
            site: context.site,
        })
    );

    const setEditProductView = (ref: any) => {
        editProductView.current = ref;
    };

    const activateClick = () => {
        if (props.product.status === 0) {
            return props.product.deactivate();
        }
        return props.product.activate();
    };

    const repost = () => {
        return props.product.repost();
    };

    const deleteFn = () => {
        confirm({
            body: <FormattedMessage id='PRODUCT_DELETE_CONFIRMATION_TEXT' />,
            onAccept: performDelete,
            acceptButtonLabel: <FormattedMessage id='BAZAAR_DELETE_PRODUCT' />,
            cancelButtonLabel: <FormattedMessage id='CANCEL' values={{ gender: 'v' }} />,
            acceptButtonType: 'warn',
        });

        return Promise.resolve();
    };

    const performDelete = () => {
        props.product
            .delete()
            .then(() => {
                acknowledge!({
                    title: <FormattedMessage id='PRODUCT_DELTETE_SUCCESS_TEXT' />,
                    onAccept: () => {
                        history.push({
                            pathname: '/market/my_bazaar/',
                            search: 'page=1',
                        });
                    },
                    acceptButtonLabel: <FormattedMessage id='CONFIRM' />,
                });
            })
            .catch(err => {
                console.log(err);
                retry!({
                    title: <FormattedMessage id='PRODUCT_DELETE_ERROR_TEXT' />,
                    onAccept: performDelete,
                    acceptButtonLabel: <FormattedMessage id='RETRY' />,
                    acceptButtonType: 'warn',
                });
            });
    };

    const edit = () => {
        if (editProductView.current && createEditProductViewModel.current) {
            const showEditProductView = () => {
                editProductView.current.show({
                    form: null,
                });
                return Promise.resolve();
            };
            context.authCheck(showEditProductView, AuthOrigin.EditBazaarProduct);
        }
        return Promise.resolve();
    };

    const toggleStarred = () => {
        const toggleStarred = () => {
            const { product } = props;
            return product.toggleStarred();
        };

        context.authCheck(toggleStarred, AuthOrigin.ToggleBazaarStar);
    };

    const reply = () => {
        context.scrollView.scrollToRef({
            ref: props.replyRef,
            offset: -40,
        });
        return Promise.resolve();
    };

    const pausePlay =
        product.status === 0 || product.status === 1 ? (
            <ActionButtonView
                styles={actionButtonStyles}
                icon={
                    product.status === 0 ? (
                        <SVGIcon style={{ image: styles.icon }} src={pause} />
                    ) : (
                        <SVGIcon style={{ image: styles.icon }} src={play} />
                    )
                }
                onClick={activateClick}
            >
                {product.status === 0 ? (
                    <FormattedMessage id='BAZAAR_INACTIVATE' />
                ) : (
                    <FormattedMessage id='BAZAAR_ACTIVATE' />
                )}
            </ActionButtonView>
        ) : null;

    return !contentHidden ? (
        <div className={styles.mainWrapper}>
            {authorView || isSuperstaff ? (
                <div className={styles.rowWrapper}>
                    <div className={styles.button}>
                        <ActionButtonView
                            icon={<SVGIcon style={{ image: styles.icon }} src={pencil} />}
                            styles={actionButtonStyles}
                            onClick={edit}
                        >
                            <FormattedMessage id='BAZAAR_EDIT_PRODUCT' />
                        </ActionButtonView>
                        <CreateEditProductView
                            innerRef={setEditProductView}
                            createEditProductViewModel={createEditProductViewModel.current}
                            forceUpdate={props.forceUpdate}
                        />
                    </div>
                    <div className={styles.button}>{pausePlay}</div>
                    <div className={styles.button}>
                        <ActionButtonView
                            icon={<SVGIcon style={{ image: styles.icon }} src={refresh} />}
                            disabled={!product.repostable}
                            styles={actionButtonStyles}
                            onClick={repost}
                        >
                            <FormattedMessage id='BAZAAR_REPOST' />
                        </ActionButtonView>
                    </div>
                    <button className={styles.deleteButton} onClick={deleteFn}>
                        <SVGIcon style={{ image: withMarginStyles.iconWithMargin }} src={trash} />
                        <span className={styles.deleteText}>
                            <FormattedMessage id='BAZAAR_DELETE_PRODUCT' />
                        </span>
                    </button>
                </div>
            ) : (
                <div className={styles.rowWrapper}>
                    <div className={styles.button}>
                        <ActionButtonView
                            icon={<SVGIcon style={{ image: styles.icon }} src={replyIcon as any} />}
                            styles={actionButtonStyles}
                            onClick={reply}
                        >
                            <FormattedMessage id='BAZAAR_SHOW_INTEREST' />
                        </ActionButtonView>
                    </div>
                    <button className={styles.favouriteButton} onClick={toggleStarred}>
                        <SVGIcon
                            style={{ image: favouriteIconStyles.favouriteIcon }}
                            src={product.isStarred ? heartFull : heart}
                        />
                        <span className={styles.deleteText}>
                            {product.isStarred ? (
                                <FormattedMessage id='BAZAAR_REMOVE_FROM_WISHLIST' />
                            ) : (
                                <FormattedMessage id='BAZAAR_ADD_TO_WISHLIST' />
                            )}
                        </span>
                    </button>
                </div>
            )}
            {!authorView && isSuperstaff ? (
                <button className={styles.favouriteButton} onClick={toggleStarred}>
                    <SVGIcon
                        style={{ image: favouriteIconStyles.favouriteIcon }}
                        src={product.isStarred ? heartFull : heart}
                    />
                    <span className={styles.deleteText}>
                        {product.isStarred ? (
                            <FormattedMessage id='BAZAAR_REMOVE_FROM_WISHLIST' />
                        ) : (
                            <FormattedMessage id='BAZAAR_ADD_TO_WISHLIST' />
                        )}
                    </span>
                </button>
            ) : null}
        </div>
    ) : (
        <div className={styles.mainWrapper}>
            <div className={styles.rowWrapper}>
                <div className={styles.button}>
                    <ActionButtonView
                        icon={<SVGIcon style={{ image: styles.icon }} src={view} />}
                        styles={actionButtonStyles}
                        onClick={() => {
                            props.showContent();
                            return Promise.resolve();
                        }}
                    >
                        <FormattedMessage id='SHOW_PRODUCT_CONTENT' />
                    </ActionButtonView>
                </div>
            </div>
        </div>
    );
};

ProductActionsBarView.contextTypes = {
    scrollView: PropTypes.object,
    site: PropTypes.instanceOf(Site),
    authCheck: PropTypes.func,
};

export default Styled(defaultStyles)(ProductActionsBarView);
